import {FC, useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {Modal} from 'react-bootstrap'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {KTIcon} from '../../../../_metronic/helpers'
import {mockProjects, Project} from '../mockData'
import {add_project, get_all_projects, update_project, delete_project} from '../api/projectsApi'
import {isProjectStarred, toggleStarredProject, getStarredProjectIds} from '../utils/starredProjects'
import {LoadingProgressBar} from './LoadingProgressBar'
import {ProjectCardSkeleton} from './ProjectCardSkeleton'
import {toast} from 'react-toastify'
import { permissionCheck } from '../../../helper-functions/PermissionCheck'

const ProjectsList: FC = () => {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [editProject, setEditProject] = useState({
    name: '',
    description: '',
    status: 'active' as 'active' | 'inactive',
    category: '',
    tags: [] as string[]
  })
  const [projectCategories, setProjectCategories] = useState<Record<string, any[]>>({})
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(6)
  const canViewProjects = Boolean(permissionCheck('project-view'))
  const canCreateProject = Boolean(permissionCheck('project-create'))
  const canUpdateProject = Boolean(permissionCheck('project-update'))
  const canDeleteProject = Boolean(permissionCheck('project-delete'))
  const canViewProjectLogs = Boolean(permissionCheck('project-activity-log'))

  // Fetch projects on component mount
  useEffect(() => {
    if (canViewProjects) {
      fetchProjects()
    }
  }, [canViewProjects])

  const fetchProjects = async () => {
    try {
      setIsLoading(true)
      const response = await get_all_projects()
      
      // API response structure: { status: true, message: "...", data: { all_projects: [...] } }
      // Check if all_projects array exists and handle empty case
      const projectsData = response?.data?.all_projects || response?.data?.projects || []
      
      if (Array.isArray(projectsData) && projectsData.length > 0) {
        const categoriesMap: Record<string, any[]> = {}
        
        // Map API response to UI format
        const mappedProjects = projectsData.map((project: any) => {
          // Calculate URL counts from project URLs if available
          const projectUrls = project.urls || project.project_urls || []
          const urlsArray = Array.isArray(projectUrls) ? projectUrls : []
          const totalUrls = urlsArray.length
          const activeUrls = urlsArray.filter((u: any) => (u.status === 1 || u.status === '1' || u.status === 'active')).length
          
          // Map categories from project response
          const projectCategoriesData = project.project_categories || []
          const categoriesArray = Array.isArray(projectCategoriesData) ? projectCategoriesData : []
          categoriesMap[project.id.toString()] = categoriesArray
          
          // Handle status: 0 = inactive, 1 = active
          const statusValue: 'active' | 'inactive' | 'draft' = project.status === 1 || project.status === '1' ? 'active' : 'inactive'
          
          return {
            id: project.id.toString(),
            name: project.project_name || '',
            description: project.description || '',
            status: statusValue,
            createdAt: project.created_at || '',
            updatedAt: project.updated_at || '',
            updatedByName: project.updated_by?.name || '',
            totalUrls: totalUrls,
            activeUrls: activeUrls,
            isStarred: isProjectStarred(project.id.toString()),
            category: '',
            tags: []
          }
        })
        
        setProjects(mappedProjects)
        setProjectCategories(categoriesMap)
      } else {
        // No projects exist, set empty array
        setProjects([])
        setProjectCategories({})
      }
    } catch (error) {
      console.error('Error fetching projects:', error)
      toast.error('Failed to load projects')
      setProjects([])
      setProjectCategories({})
    } finally {
      setIsLoading(false)
    }
  }


  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.category.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Pagination derived values
  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / pageSize))
  const pageSafe = Math.min(currentPage, totalPages)
  const startIdx = (pageSafe - 1) * pageSize
  const endIdx = startIdx + pageSize
  const paginatedProjects = filteredProjects.slice(startIdx, endIdx)

  // Reset to page 1 on search or page size change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, pageSize])

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/detail/${projectId}`)
  }

  const toggleStar = (projectId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    try {
      const isNowStarred = toggleStarredProject(projectId)
      setProjects(prev => prev.map(project => 
        project.id === projectId 
          ? {...project, isStarred: isNowStarred}
          : project
      ))
      if (isNowStarred) {
        toast.success('Project added to starred')
      } else {
        toast.info('Project removed from starred')
      }
      // Verify it was saved
      const savedIds = getStarredProjectIds()
      console.log('Starred projects after toggle:', savedIds)
    } catch (error) {
      console.error('Error toggling star:', error)
      toast.error('Failed to update starred status')
    }
  }

  const handleEditClick = (project: Project, event: React.MouseEvent) => {
    if (!canUpdateProject) {
      toast.error('You do not have permission to update projects')
      return
    }
    event.stopPropagation()
    setProjectToEdit(project)
    // Convert draft status to inactive since draft is no longer available
    const projectStatus = project.status === 'draft' ? 'inactive' : project.status
    setEditProject({
      name: project.name,
      description: project.description,
      status: projectStatus as 'active' | 'inactive',
      category: project.category,
      tags: project.tags
    })
    setShowEditModal(true)
  }

  const handleDeleteClick = (project: Project, event: React.MouseEvent) => {
    if (!canDeleteProject) {
      toast.error('You do not have permission to delete projects')
      return
    }
    event.stopPropagation()
    setProjectToDelete(project)
    setShowDeleteModal(true)
  }

  const handleProjectLogs = (project: Project) => {
    navigate("/projects/single-project-log", { state: { id: project.id } });
  }

  const handleDeleteConfirm = async () => {
    if (!canDeleteProject) {
      toast.error('You do not have permission to delete projects')
      return
    }
    if (projectToDelete) {
      try {
        setIsLoading(true)
        const response = await delete_project(projectToDelete.id)
        
        // Check if response has a message (could be success or error message)
        const responseMessage = response?.message || response?.data?.message
        
        if (responseMessage) {
          // Show the message from API response
          // Check if message indicates failure (contains words like "cannot", "failed", "error", etc.)
          const isError = responseMessage.toLowerCase().includes('cannot') || 
                         responseMessage.toLowerCase().includes('failed') || 
                         responseMessage.toLowerCase().includes('error') ||
                         responseMessage.toLowerCase().includes('not allowed')
          
          if (isError || !response?.status) {
            toast.error(responseMessage)
          } else {
            toast.success(responseMessage)
            // Only remove from list if it was actually deleted
            setProjects(projects.filter(proj => proj.id !== projectToDelete.id))
            setShowDeleteModal(false)
            setProjectToDelete(null)
          }
        } else if (response?.status) {
          // No message but status is true - assume success
          toast.success('Project deleted successfully')
          setProjects(projects.filter(proj => proj.id !== projectToDelete.id))
          setShowDeleteModal(false)
          setProjectToDelete(null)
        } else {
          toast.error('Failed to delete project')
        }
      } catch (error: any) {
        console.error('Error deleting project:', error)
        // Check if error response has a message
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete project'
        toast.error(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }
  }


  const handleEditInputChange = (field: string, value: string) => {
    setEditProject(prev => ({ ...prev, [field]: value }))
  }

  const handleEditTagsChange = (value: string) => {
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag)
    setEditProject(prev => ({ ...prev, tags }))
  }

  const handleUpdateProject = async () => {
    if (!canUpdateProject) {
      toast.error('You do not have permission to update projects')
      return
    }
    if (!projectToEdit) return
    
    // Validation for required fields
    if (!editProject.name.trim() || !editProject.status) {
      toast.error('Please fill all required fields')
      return
    }

    try {
      setIsLoading(true)
      // Prepare API payload
      // Convert status to "0"/"1" format as string: active="1", inactive="0"
      const statusValue = editProject.status === 'active' ? '1' : '0'
      
      const apiPayload = {
        project_name: editProject.name,
        description: editProject.description || '',
        status: statusValue,
        // Additional fields from UI that will be sent
        category: editProject.category || '',
        tags: editProject.tags || []
      }
      
      const response = await update_project(projectToEdit.id, apiPayload)
      toast.success('Project updated successfully')
      setShowEditModal(false)
      setProjectToEdit(null)
      // Refresh the list
      await fetchProjects()
    } catch (error: any) {
      console.error('Error updating project:', error)
      // Show backend error message if available
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update project'
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: {class: 'badge-light-success', text: 'Active'},
      inactive: {class: 'badge-light-danger', text: 'Inactive'},
      draft: {class: 'badge-light-warning', text: 'Draft'}
    }
    
    const config = statusConfig[status as keyof typeof statusConfig]
    return (
      <span className={`badge ${config.class} fw-bold`}>
        {config.text}
      </span>
    )
  }

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  if (!canViewProjects) {
    return (
      <div className='container-xxl py-10'>
        <div className='alert alert-warning'>
          You do not have permission to view projects.
        </div>
      </div>
    )
  }

  return (
    <div className='d-flex flex-column flex-column-fluid'>
      {/* Loading Progress Bar */}
      <LoadingProgressBar isLoading={isLoading} />
      
      {/* Header */}
      <div className='toolbar' id='kt_toolbar'>
        <div id='kt_toolbar_container' className='container-fluid d-flex flex-stack justify-content-start align-items-start'>
          <div className='page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0 flex-shrink-0'>
            <h1 className='d-flex align-items-center text-dark fw-bolder fs-3 my-1'>
              Projects
              <span className='h-20px border-gray-200 border-start ms-3 mx-2'></span>
              <small className='text-muted fs-7 fw-bold my-1 ms-1'>Manage your projects and subprojects</small>
            </h1>
          </div>
          <div className='d-flex align-items-center gap-2 gap-lg-3 flex-shrink-0 ms-auto'>
            {canCreateProject && (
              <button 
                className='btn btn-sm btn-primary'
                onClick={() => navigate('/projects/create')}
              >
                <KTIcon iconName='plus' className='fs-2' />
                Create Project
              </button>
            )}
            <button 
              className='btn btn-sm btn-light-primary'
              onClick={() => navigate('/projects/starred')}
            >
              <KTIcon iconName='star' className='fs-2' />
              Starred Projects
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div id='kt_content' className='content flex-column-fluid'>
        <div className='container-xxl'>
          {/* Search and Filter */}
          <div className='card mb-5'>
            <div className='card-body'>
              <div className='d-flex align-items-center position-relative my-1'>
                <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-5' />
                <input
                  type='text'
                  className='form-control form-control-solid w-250px ps-13'
                  placeholder='Search projects...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Projects Grid */}
          <div className='row g-5 g-xl-8'>
            {isLoading && projects.length === 0 ? (
              // Show skeleton loaders while loading
              <>
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
              </>
            ) : (
              paginatedProjects.map((project) => (
              <div key={project.id} className='col-xl-4 col-lg-6 col-md-6'>
                <KTCard className='card-xl-stretch mb-xl-8 h-100'>
                  <KTCardBody className='d-flex flex-column p-6'>
                    {/* Header */}
                    <div className='d-flex align-items-center justify-content-between mb-4'>
                      <div className='d-flex align-items-center'>
                        <div className='symbol symbol-50px me-3'>
                          <div className='symbol-label bg-light-primary'>
                            <KTIcon iconName='abstract-26' className='fs-2x text-primary' />
                          </div>
                        </div>
                        <div>
                          <h3 className='fs-4 fw-bolder text-gray-900 mb-1'>{project.name}</h3>
                          <span className='text-muted fw-bold text-muted d-block fs-7'>
                            {project.category}
                          </span>
                        </div>
                      </div>
                      <div className='btn-group'>
                        <button
                          type='button'
                          className={`btn btn-sm ${project.isStarred ? 'btn-warning' : 'btn-light'}`}
                          onClick={(e) => toggleStar(project.id, e)}
                          title={project.isStarred ? 'Remove from starred' : 'Add to starred'}
                        >
                          <KTIcon iconName='star' className='fs-5' />
                        </button>
                        {(canUpdateProject || canDeleteProject) && (
                          <>
                            <button
                              type='button'
                              className='btn btn-sm btn-light dropdown-toggle dropdown-toggle-split'
                              data-bs-toggle='dropdown'
                              aria-expanded='false'
                            >
                              <span className='visually-hidden'>Toggle Dropdown</span>
                            </button>
                            <ul className='dropdown-menu dropdown-menu-end'>
                              {/* {canViewProjectLogs && (
                                <li>
                                  <button 
                                    className='dropdown-item' 
                                    onClick={() => handleProjectLogs(project)}
                                  >
                                    <i className='bi bi-journal-text fs-6 me-2'></i>
                                    Activity Logs
                                  </button>
                                </li>
                              )}
                              {canViewProjectLogs && (canUpdateProject || canDeleteProject) && (
                                <li>
                                  <hr className='dropdown-divider' />
                                </li>
                              )} */}
                              {canUpdateProject && (
                                <li>
                                  <button 
                                    className='dropdown-item' 
                                    onClick={(e) => handleEditClick(project, e)}
                                  >
                                    <KTIcon iconName='pencil' className='fs-6 me-2' />
                                    Edit
                                  </button>
                                </li>
                              )}
                              {canUpdateProject && canDeleteProject && (
                                <li>
                                  <hr className='dropdown-divider' />
                                </li>
                              )}
                              {canDeleteProject && (
                                <li>
                                  <button 
                                    className='dropdown-item text-danger' 
                                    onClick={(e) => handleDeleteClick(project, e)}
                                  >
                                    <KTIcon iconName='trash' className='fs-6 me-2' />
                                    Delete
                                  </button>
                                </li>
                              )}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <p 
                      className='text-gray-600 fs-6 mb-4 flex-grow-1' 
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={project.description || 'N/A'}
                    >
                      {project.description || 'N/A'}
                    </p>

                    {/* Categories Tags */}
                    <div className='mb-4'>
                      {(projectCategories[project.id] || [])
                        .filter((cat: any) => cat.status === 1 || cat.status === '1')
                        .map((cat: any) => (
                          <span key={cat.id} className='badge badge-light-primary me-1 mb-1'>
                            {cat.category_name}
                          </span>
                        ))}
                    </div>

                    {/* Status and Action */}
                    <div className='d-flex align-items-center justify-content-between mb-3'>
                      {getStatusBadge(project.status)}
                      <button
                        className='btn btn-sm btn-primary'
                        onClick={() => handleProjectClick(project.id)}
                      >
                        View Details
                        <KTIcon iconName='arrow-right' className='fs-2 ms-1' />
                      </button>
                    </div>

                    {/* Meta */}
                    <div className='text-muted fs-7'>
                      <div className='d-flex flex-wrap gap-3'>
                        <span>Updated: {new Date(project.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </KTCardBody>
                </KTCard>
              </div>
            ))
            )}
          </div>

          {/* Pagination controls */}
          <div className='d-flex align-items-center justify-content-between mt-4'>
            <div className='d-flex align-items-center gap-3'>
              <span className='text-muted fs-7'>
                Showing {filteredProjects.length === 0 ? 0 : startIdx + 1}-{Math.min(endIdx, filteredProjects.length)} of {filteredProjects.length}
              </span>
              <div className='d-flex align-items-center'>
                <span className='me-2 text-muted fs-7'>Show</span>
                <select
                  className='form-select form-select-sm w-auto'
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                </select>
                <span className='ms-2 text-muted fs-7'>per page</span>
              </div>
            </div>
            <ul className='pagination'>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(1)}>«</button>
              </li>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
              </li>
              <li className='page-item active'>
                <span className='page-link'>{pageSafe} / {totalPages}</span>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(totalPages)}>»</button>
              </li>
            </ul>
          </div>

          {/* Empty State */}
          {!isLoading && filteredProjects.length === 0 && (
            <div className='text-center py-10'>
              <KTIcon iconName='abstract-26' className='fs-3x text-muted mb-5' />
              <h3 className='text-gray-900 mb-3'>No projects found</h3>
              <p className='text-muted fs-6'>
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : canCreateProject
                  ? 'Create your first project to get started'
                  : 'You currently do not have permission to create new projects.'}
              </p>
            </div>
          )}
        </div>
      </div>


      {/* Edit Project Modal */}
      {canUpdateProject && showEditModal && (
        <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h2 className='modal-title'>Edit Project</h2>
                <button 
                  type='button' 
                  className='btn-close' 
                  onClick={() => {
                    setShowEditModal(false)
                    setProjectToEdit(null)
                  }}
                ></button>
              </div>
              <div className='modal-body'>
                <form>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Project Name <span className='text-danger'>*</span></label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Enter project name'
                      value={editProject.name}
                      onChange={(e) => handleEditInputChange('name', e.target.value)}
                      required
                    />
                  </div>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Description</label>
                    <textarea
                      className='form-control'
                      rows={3}
                      placeholder='Enter project description'
                      value={editProject.description}
                      onChange={(e) => handleEditInputChange('description', e.target.value)}
                    ></textarea>
                  </div>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Status <span className='text-danger'>*</span></label>
                    <select
                      className='form-select'
                      value={editProject.status}
                      onChange={(e) => handleEditInputChange('status', e.target.value)}
                    >
                      <option value='active'>Active</option>
                      <option value='inactive'>Inactive</option>
                    </select>
                  </div>
                  {/* TODO: Uncomment when category functionality is needed */}
                  {/* <div className='mb-5'>
                    <label className='form-label fw-bold'>Category</label>
                    <select
                      className='form-select'
                      value={editProject.category}
                      onChange={(e) => handleEditInputChange('category', e.target.value)}
                    >
                      <option value=''>Select category</option>
                      <option value='Business'>Business</option>
                      <option value='Marketing'>Marketing</option>
                      <option value='Personal'>Personal</option>
                      <option value='Education'>Education</option>
                      <option value='Other'>Other</option>
                    </select>
                  </div>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Tags (comma separated)</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='e.g., web, mobile, api'
                      value={editProject.tags.join(', ')}
                      onChange={(e) => handleEditTagsChange(e.target.value)}
                    />
                  </div> */}
                </form>
              </div>
              <div className='modal-footer'>
                <button 
                  type='button' 
                  className='btn btn-light' 
                  onClick={() => {
                    setShowEditModal(false)
                    setProjectToEdit(null)
                  }}
                >
                  Cancel
                </button>
                <button 
                  type='button' 
                  className='btn btn-primary' 
                  onClick={handleUpdateProject}
                >
                  Update Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        className='modal fade'
        id='kt_modal_delete_project'
        tabIndex={-1}
        role='dialog'
        show={canDeleteProject && showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false)
          setProjectToDelete(null)
        }}
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <div
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => {
                  setShowDeleteModal(false)
                  setProjectToDelete(null)
                }}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>
            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              <div className='mb-13 text-center'>
                <KTIcon iconName='question' className='fs-5tx text-danger mb-5' />
                <h1 className='mb-3'>Delete Project</h1>
                <div className='text-muted fw-semibold fs-5'>
                  Are you sure you want to delete this project?
                  {projectToDelete && (
                    <div className='mt-3 mb-3'>
                      <strong className='text-dark fs-4'>"{projectToDelete.name}"</strong>
                    </div>
                  )}
                  <div className='mt-4'>
                    <p className='mb-2'>This action cannot be undone.</p>
                    <p className='mb-0'>All subprojects and associated data in this project will also be permanently deleted.</p>
                  </div>
                </div>
              </div>
              <div className='d-flex flex-stack'>
                <button
                  className='btn btn-primary'
                  onClick={() => {
                    setShowDeleteModal(false)
                    setProjectToDelete(null)
                  }}
                >
                  Cancel
                </button>
                <button className='btn btn-danger' onClick={handleDeleteConfirm}>
                  Delete Project
                </button>
              </div>
            </div>
          </div>
        </div>
        </Modal>
    </div>
  )
}

export {ProjectsList}
